import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService, TranslateStore } from '@ngx-translate/core';
import { startWith } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LocalizeParser, LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { environment } from '@environment';

/** Context handed to a route's `jsonLd` factory. */
export interface JsonLdContext {
  /** Resolves a translation key against the active language. */
  t: (key: string) => string;
  lang: string;
  baseUrl: string;
  /** Absolute URL of the current route, without query or hash. */
  url: string;
}

export type JsonLdFactory = (ctx: JsonLdContext) => object;

@Injectable({
  providedIn: 'root'
})
export class MetaService {

  /** og:locale values per supported language */
  private static readonly OG_LOCALES: { [lang: string]: string } = {
    en: 'en_US', de: 'de_DE', fr: 'fr_FR', es: 'es_ES', it: 'it_IT', pl: 'pl_PL'
  };

  /**
   * First path segment (after the language prefix) of everything that must stay out of the index.
   * Covers the localized member slugs from ROUTES.member of all supported languages.
   */
  private static readonly NO_INDEX_SEGMENTS = [
    'member', 'membre', 'miembro', 'membro', 'strefa-klienta', 'ssr-loading'
  ];

  private static readonly NO_INDEX_RE =
    new RegExp(`^/[a-z]{2}/(${MetaService.NO_INDEX_SEGMENTS.join('|')})(/|$)`);

  private altHrefLinks: HTMLLinkElement[] = [];
  private canonicalLink?: HTMLLinkElement;
  private jsonLdScript?: HTMLScriptElement;

  constructor(@Inject(DOCUMENT) private document: Document,
              private translate: TranslateService,
              private translateStore: TranslateStore,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private titleService: Title,
              private metaService: Meta,
              private localize: LocalizeRouterService,
              public parser: LocalizeParser) { }

  public init(){


    // Update the language in the lang attribute of the html element.
    this.translate.onLangChange
      .pipe(startWith(true))
      .subscribe(() => {
        this.document.documentElement.lang = this.translate.currentLang;
      });

    // Update meta tags and title
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(startWith(true))
      .subscribe(() => {

        this.updateAltLangTags();
        this.updateCanonicalLink();

        const rt = this.getChild(this.activatedRoute)
        rt.data.subscribe((data: any) => {
          if (data.title) {
            this.titleService.setTitle(this.translate.instant(data.title));
            this.metaService.updateTag({ property: 'og:title', content: this.translate.instant(data.title) })
            this.metaService.updateTag({ name: 'twitter:title', content: this.translate.instant(data.title) })
          } else {
            this.titleService.setTitle('dewabit');
            this.metaService.removeTag("property='og:title'")
            this.metaService.removeTag("name='twitter:title'")
          }
          if (data.description) {
            this.metaService.updateTag({ name: 'description', content: this.translate.instant(data.description) })
            this.metaService.updateTag({ property: 'og:description', content: this.translate.instant(data.description) })
            this.metaService.updateTag({ name: 'twitter:description', content: this.translate.instant(data.description) })
          } else {
            this.metaService.removeTag("name='description'")
            this.metaService.removeTag("property='og:description'")
            this.metaService.removeTag("name='twitter:description'")
          }
          if (data.keywords) {
            this.metaService.updateTag({ name: 'keywords', content: this.translate.instant(data.keywords) })
          } else {
            this.metaService.removeTag("name='keywords'")
          }

          // Member area and technical routes must not be indexed
          const noIndex = data.noIndex || MetaService.NO_INDEX_RE.test(this.router.url) ||
            this.router.url.startsWith('/client-') || this.router.url.startsWith('/redirect');
          // Let Google show large image previews and full-length text/video snippets on the
          // indexable pages; noindex pages stay fully excluded.
          this.metaService.updateTag({ name: 'robots', content: noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' })

          const absoluteUrl = environment.baseUrl + this.router.url.split('?')[0].split('#')[0];
          this.metaService.updateTag({ property: 'og:url', content: absoluteUrl })
          this.metaService.updateTag({ property: 'og:type', content: 'website' })
          this.metaService.updateTag({ property: 'og:site_name', content: 'dewabit' })
          this.metaService.updateTag({ property: 'og:locale', content: this.ogLocale(this.translate.currentLang) })

          const ogImage = data.ogImage || environment.baseUrl + '/assets/images/description-logos/dewabit_banner_' + (this.translate.currentLang === 'de' ? 'de' : 'en') + '.png';
          this.metaService.updateTag({ property: 'og:image', content: ogImage })
          this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' })
          this.metaService.updateTag({ name: 'twitter:image', content: ogImage })

          this.updateJsonLd(data.jsonLd, noIndex);
        })

      })
  }

  private ogLocale(lang: string): string {
    return MetaService.OG_LOCALES[lang] ?? MetaService.OG_LOCALES['en'];
  }

  /**
   * Renders the route's structured data into a single <script type="application/ld+json"> in the head.
   * Routes provide a `jsonLd` factory in their route data so the schema can use translated strings.
   * The block is dropped on noindex routes and on routes that don't declare one.
   */
  private updateJsonLd(factory: JsonLdFactory | undefined, noIndex: boolean) {
    if (!this.jsonLdScript) {
      // reuse the element from the prerendered document to avoid duplicates
      this.jsonLdScript = this.document.head
        .querySelector('script[type="application/ld+json"][data-route-schema]') as HTMLScriptElement ?? undefined;
    }

    if (!factory || noIndex) {
      this.jsonLdScript?.parentNode?.removeChild(this.jsonLdScript);
      this.jsonLdScript = undefined;
      return;
    }

    const schema = factory({
      t: (key: string) => this.translate.instant(key),
      lang: this.translate.currentLang,
      baseUrl: environment.baseUrl,
      url: environment.baseUrl + this.router.url.split('?')[0].split('#')[0],
    });

    if (!this.jsonLdScript) {
      this.jsonLdScript = this.document.createElement('script');
      this.jsonLdScript.setAttribute('type', 'application/ld+json');
      this.jsonLdScript.setAttribute('data-route-schema', '');
      this.document.head.appendChild(this.jsonLdScript);
    }
    this.jsonLdScript.textContent = JSON.stringify(schema);
  }

  /**
   * Overrides the canonical URL of the current page.
   * For pages whose canonical is only known once async content has loaded — a blog post exists in
   * one language but is reachable under every language prefix, so it must point at its own version.
   * Runs after updateCanonicalLink() of the current navigation, so the override wins.
   */
  public setCanonical(absoluteUrl: string) {
    this.writeCanonical(absoluteUrl);
  }

  private updateCanonicalLink() {
    this.writeCanonical(environment.baseUrl + this.router.url.split('?')[0].split('#')[0]);
  }

  private writeCanonical(url: string) {
    if (!this.canonicalLink) {
      // reuse the element from the prerendered document to avoid duplicates
      this.canonicalLink = this.document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement
        ?? this.document.createElement('link');
      this.canonicalLink.setAttribute('rel', 'canonical');
      if (!this.canonicalLink.parentNode) {
        this.document.head.appendChild(this.canonicalLink);
      }
    }
    this.canonicalLink.setAttribute('href', url);
  }

  private getChild(activatedRoute: ActivatedRoute): any {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  private updateAltLangTags() {

    let validPath = true;
    const pathSegments: string[] = [];
    let path = '';

    const child = (activatedRoute: ActivatedRoute): any  => {
      if (activatedRoute.firstChild) {
        if (activatedRoute.firstChild.snapshot.url?.length) {
          if (activatedRoute.firstChild.snapshot.data['skipRouteLocalization']) {
            validPath = false;
          } else {
            const lPath = activatedRoute.firstChild.snapshot.data['localizeRouter']?.path;
            if (lPath) {
              pathSegments.push(lPath);
              path += '/' + lPath;
            }
          }
        }
        child(activatedRoute.firstChild);
      }
    }

    child(this.activatedRoute);

    // remove our own links AND any alternates baked into the prerendered document
    this.altHrefLinks.forEach(le => {
      le.parentNode?.removeChild(le);
    });
    this.document.head.querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach(le => le.parentNode?.removeChild(le));
    this.altHrefLinks = [];


    if (!validPath) {
      return;
    }

    const alt: {language: string, link: string}[] = [];

    this.localize.parser.locales.forEach(locale => {
      const localeTranslations = this.translateStore.getTranslations(locale) as any;
      const rt = pathSegments.map(s => localeTranslations?.[this.localize.parser.getPrefix().slice(0, -1)]?.[s]).join('/');
      alt.push({language: locale, link: `/${locale}/` + rt});
      // english version doubles as the x-default for unmatched languages
      if (locale === 'en') {
        alt.push({language: 'x-default', link: `/${locale}/` + rt});
      }
    });

    alt.forEach(a => {
      const linkElt = this.document.createElement('link');
      linkElt.setAttribute( 'rel', 'alternate');
      linkElt.setAttribute( 'hreflang', a.language);
      linkElt.setAttribute( 'href', environment.baseUrl + a.link);
      this.altHrefLinks.push(linkElt);
      this.document.head.appendChild(linkElt);
    });

  }

}
